// Foto prodotto per le righe del carrello (redesign 2026-10, fase 2).
// Le righe salvate negli ordini non hanno un campo immagine, quindi la foto si ricava:
//  1. dall'immagine già presente sulla riga (item.image, es. alimentatore suggerito)
//  2. dalla configurazione del profilo (datasheetConfig.images.imagePath = foto della finitura scelta)
//  3. dal codice prodotto, cercandolo nei cataloghi del B2B (catalogo, alimentatori, strip, BTM,
//     modelli e accessori dei profili). I cataloghi si scaricano una volta sola per sessione.
import { useEffect, useState } from 'react';
import { urlAsset } from './utils';

const SOURCES = {
  catalog: '/api/products/public',
  powerSupplies: '/api/products/config/alimentatori',
  strips: '/api/products/config/stripled',
  btm: '/api/products/config/btmConfig',
  profiles: '/api/products/config/config',
  looseProfiles: '/api/products/config/configlooseprofiles',
};

let indexPromise = null;

// Scelte di un prodotto a catalogo, nell'ordine in cui compaiono nel codice riga.
export const CATALOG_OPTION_KEYS = ['colore_LED', 'ottica', 'finitura', 'power'];
const parseOptionList = (value) => {
  if (typeof value !== 'string') return Array.isArray(value) ? value : [];
  try { const v = JSON.parse(value); return Array.isArray(v) ? v : []; } catch (e) { return []; }
};
const hasCatalogVariants = (p) => CATALOG_OPTION_KEYS.some((k) => parseOptionList(p[k]).length > 1);

// Riga del Catalogo: "<id>-<colore>-<ottica>-<finitura>-<potenza>" (l'id può contenere "-").
// Restituisce l'id del prodotto (il più lungo che combacia) solo se il prodotto ha varianti.
export const findCatalogProductId = (code, ids) => {
  const c = String(code || '').trim();
  return (ids || []).find((id) => c.startsWith(`${id}-`) && c.slice(id.length + 1).split('-').length === 4) || null;
};

const getJson = (url) =>
  fetch(url)
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null);

const modelCodeOf = (name, codes) =>
  codes?.[name] || String(name || '').replace(/\D/g, '') || String(name || '').replace(/\s+/g, '').toUpperCase();

const buildIndex = async () => {
  const [catalog, powerSupplies, strips, btm, profiles, looseProfiles] = await Promise.all(
    Object.values(SOURCES).map(getJson)
  );
  const exact = new Map();   // codice esatto -> immagine
  const prefixes = [];       // [prefisso, immagine], si usa il prefisso più lungo
  const accessories = [];    // [prefissoModello, chiave, immagine] per codici tipo "2038-Z85"
  const catalogIds = [];     // id dei prodotti a catalogo con almeno una scelta (colore luce, finitura, ...)

  (Array.isArray(powerSupplies) ? powerSupplies : []).forEach((p) => p?.id && p.image && exact.set(String(p.id), p.image));
  (Array.isArray(strips) ? strips : []).forEach((s) => s?.code && s.image && exact.set(String(s.code), s.image));
  // Catalogo: il codice in carrello è "<id>-<colore>-<ottica>-<finitura>-<potenza>"
  (Array.isArray(catalog) ? catalog : []).forEach((p) => p?.id && p.image && prefixes.push([`${p.id}-`, p.image]));
  (Array.isArray(catalog) ? catalog : []).forEach((p) => p?.id && hasCatalogVariants(p) && catalogIds.push(String(p.id)));
  // BTM: il codice è "<id><lunghezza><colore>"
  ['products', 'accessories', 'binari'].forEach((k) =>
    (btm?.[k] || []).forEach((p) => p?.id && p.image && prefixes.push([String(p.id), p.image]))
  );
  // Profili (su misura e sciolti): il codice inizia con il codice modello (cifre del nome)
  [profiles, looseProfiles].forEach((cfg) => {
    if (!cfg?.models) return;
    Object.values(cfg.models).flat().forEach((m) => {
      if (!m?.name || !m.image) return;
      const code = modelCodeOf(m.name, cfg.modelCodes);
      if (code.length >= 4) prefixes.push([code, m.image]);
    });
    Object.entries(cfg.modelAccessories || {}).forEach(([modelName, list]) => {
      (list || []).forEach((a) => {
        if (!a?.key || !a.image) return;
        accessories.push([modelCodeOf(modelName, cfg.modelCodes), a.key, a.image]);
        accessories.push([modelName, a.key, a.image]); // profili sciolti: prefisso = nome modello
      });
    });
  });
  prefixes.sort((a, b) => b[0].length - a[0].length);
  catalogIds.sort((a, b) => b.length - a.length);
  return { exact, prefixes, accessories, catalogIds };
};

export const loadIndex = () => {
  if (!indexPromise) indexPromise = buildIndex();
  return indexPromise;
};

// Percorso immagine della riga (relativo, es. "images/bmn_bianco.jpg") oppure null.
export const findItemImage = (item, index) => {
  if (!item || item.type === 'adjustment') return null;
  if (item.image) return item.image;
  const configured = item.datasheetConfig?.images?.imagePath;
  if (configured) return configured;
  if (!index) return null;
  const code = String(item.code || '').trim();
  if (!code) return null;
  if (index.exact.has(code)) return index.exact.get(code);
  // Accessori profilo: "<modello>-<chiave><colore>" (es. "2038-Z85", "BMN 2038-SOSP6")
  const dash = code.lastIndexOf('-');
  if (dash > 0) {
    const model = code.slice(0, dash);
    const rest = code.slice(dash + 1);
    const hit = index.accessories.find(([m, key]) => m === model && rest.startsWith(key));
    if (hit) return hit[2];
  }
  const pre = index.prefixes.find(([p]) => code.startsWith(p));
  return pre ? pre[1] : null;
};

// Hook: restituisce una funzione item -> URL immagine (o null) e si aggiorna quando i cataloghi sono caricati.
export const useItemImage = () => {
  const [index, setIndex] = useState(null);
  useEffect(() => {
    let alive = true;
    loadIndex().then((idx) => {
      if (alive) setIndex(idx);
    });
    return () => {
      alive = false;
    };
  }, []);
  return (item) => {
    const path = findItemImage(item, index);
    return path ? urlAsset(path) : null;
  };
};

// Hook: item -> true se la riga è un prodotto del Catalogo con varianti (riapribile con "Modifica").
export const useCatalogEditable = () => {
  const [ids, setIds] = useState(null);
  useEffect(() => {
    let alive = true;
    loadIndex().then((idx) => {
      if (alive) setIds(idx.catalogIds);
    });
    return () => {
      alive = false;
    };
  }, []);
  return (item) => !!ids && item?.type !== 'adjustment' && !!findCatalogProductId(item?.code, ids);
};
